<script setup>
import PageWrapper from '@/components/shared/page/PageWrapper.vue'
import AppVersion from '@/components/app/AppVersion.vue'
import FlexColumn from '@/components/shared/page/FlexColumn.vue'
import NoContent from '@/components/shared/page/NoContent.vue'
import SecondaryHeading from '@/components/shared/page/SecondaryHeading.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import ChannelCard from '@/components/enrollments/ChannelCard.vue'
import EnrollmentCard from '@/components/enrollments/EnrollmentCard.vue'
import SearchInput from '@/components/shared/form/SearchInput.vue'
import SelectInput from '@/components/shared/form/SelectInput.vue'
import Alert from '@/components/shared/core/Alert.vue'
import Icon from '@/components/shared/icons/Icon.vue'
import {enrollmentStatuses, enrollmentFilterOptions} from '@/constants/enrollments.js'
import {useChannelsQuerying} from '@/composables/use-channels-querying.js'
import {useEnrollmentsQuerying} from '@/composables/use-enrollments-querying.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useTeams} from '@/composables/use-teams.js'
import {useEnv} from '@/composables/use-env.js'
import {useSearch} from '@/composables/use-search.js'
import {useValueToggle} from '@/composables/use-value-toggle.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useInfiniteScroll} from '@vueuse/core'
import {ref, computed, onMounted, onUnmounted} from 'vue'

//Composables
const {isInTeams, teamsAppVersion, promptTeamsAppInstall} = useTeams()
const {teamsAppVersion: teamsAppVersionForOrg, showTeamsAppVersionWarning} = useCurrentOrganization()
const {teamsAppIdV3} = useEnv()
const {goToConversationForChannel, goToConversationForEnrollment} = useNavigation()

//Teams warning
const shouldShowTeamsAlert = computed(() => (
  isInTeams.value && showTeamsAppVersionWarning.value &&
  teamsAppVersionForOrg.value === 3 && teamsAppVersion.value < 3
))

//Handle Teams app install prompt
const handleTeamsAppInstall = event => {
  event.preventDefault()
  event.stopPropagation()
  promptTeamsAppInstall(teamsAppIdV3)
}

//Channel querying
const {
  channels,
  loadChannels,
} = useChannelsQuerying()

//Enrollment querying
const {
  enrollments,
  loadEnrollments,
  loadMoreEnrollments,
  isLoadingEnrollments,
  isLoadingMoreEnrollments,
  hasLoadedAllEnrollments,
  changeSearch,
  changeFilter,
} = useEnrollmentsQuerying()

//Toggle app version information
const {
  isValue: isShowingAppVersion,
  setTrue: showAppVersion,
} = useValueToggle()

//Refs
const statusFilter = ref('default')

//State filter changes
const setStatusFilter = value => {
  statusFilter.value = value

  //Update the actual filter accordingly
  if (value === 'default') {
    changeFilter({
      isRecent: true,
      status: null,
    })
  }
  else if (value === 'active') {
    changeFilter({
      isRecent: null,
      status: [
        enrollmentStatuses.ACTIVE,
        enrollmentStatuses.NOT_ENGAGED,
      ].join(','),
    })
  }
  else if (value === 'paused') {
    changeFilter({
      isRecent: null,
      status: [
        enrollmentStatuses.PAUSED,
      ].join(','),
    })
  }
  else if (value === 'archived') {
    changeFilter({
      isRecent: null,
      status: [
        enrollmentStatuses.CANCELED,
        enrollmentStatuses.COMPLETED,
        enrollmentStatuses.DISABLED,
        enrollmentStatuses.OPTED_OUT,
        enrollmentStatuses.WHATSAPP_WINDOW_VIOLATED,
        enrollmentStatuses.ERROR,
      ].join(','),
    })
  }
}

//Infinite scroll
const pageWrapper = ref(null)
const main = computed(() => pageWrapper.value?.main)
useInfiniteScroll(main, loadMoreEnrollments, {
  distance: 10,
  canLoadMore: () => !hasLoadedAllEnrollments.value,
})

//Searching
const fullSearchHandler = search => changeSearch(search)
const {
  searchTerm,
  fullSearch,
  clearSearch,
} = useSearch(fullSearchHandler)

//Poll for new data
const pollInterval = ref(null)
const startPolling = () => {
  stopPolling()
  pollInterval.value = setInterval(() => {
    loadChannels()
    loadEnrollments()
  }, 10000)
}
const stopPolling = () => {
  clearInterval(pollInterval.value)
  pollInterval.value = null
}

//Load enrollment groups on mount
onMounted(() => {
  loadChannels()
  loadEnrollments()
  startPolling()
})

//Stop polling when component is unmounted
onUnmounted(stopPolling)
</script>

<template>
  <PageWrapper
    ref="pageWrapper"
    is-transparent
  >
    <Alert
      v-if="shouldShowTeamsAlert"
      type="warning"
    >
      This version of the Arist Teams app will be discontinued shortly. Please <a
        @click="handleTeamsAppInstall($event)"
      >click here to use the new Arist Teams app</a>.
    </Alert>
    <template v-if="channels.length > 0">
      <SecondaryHeading
        text="My channels"
        tag="h2"
        :class="$style.heading"
      />

      <FlexColumn
        :class="$style.channels"
      >
        <ChannelCard
          v-for="channel in channels"
          :key="channel.id"
          :channel="channel"
          :is-unread="channel.isUnread"
          @click="goToConversationForChannel(channel)"
        />
      </FlexColumn>
    </template>

    <SecondaryHeading
      text="My enrollments"
      tag="h2"
      :class="$style.heading"
    />
    <div :class="$style.searchAndFilter">
      <div :class="$style.search">
        <small text-muted>&nbsp;</small>
        <SearchInput
          ref="input"
          :search-term="searchTerm"
          placeholder="Search enrollments"
          select-on-focus
          :is-disabled="isLoadingEnrollments"
          @clear-search="clearSearch()"
          @full-search="fullSearch($event.searchTerm)"
        />
      </div>

      <div :class="$style.filter">
        <small text-muted>Filter by status</small>
        <SelectInput
          placeholder="Filter"
          aria-label="Filter by status"
          :options="enrollmentFilterOptions"
          :value="statusFilter"
          :is-disabled="isLoadingEnrollments"
          @input="setStatusFilter($event.value)"
        />
      </div>
    </div>

    <Spinner v-if="isLoadingEnrollments" />

    <NoContent
      v-else-if="enrollments.length === 0"
      text="No enrollments"
    />
    <FlexColumn
      v-else
      :class="$style.enrollments"
    >
      <EnrollmentCard
        v-for="enrollment in enrollments"
        :key="enrollment.id"
        :enrollment="enrollment"
        :is-unread="enrollment.isUnread"
        @click="goToConversationForEnrollment(enrollment)"
      />
      <Spinner
        v-if="isLoadingMoreEnrollments"
        is-block
      />
    </FlexColumn>

    <AppVersion v-if="isShowingAppVersion" />
    <Icon
      v-else
      icon="info"
      is-outlined
      size="xs"
      title="Show app version information"
      :class="$style.appVersionIcon"
      @click="showAppVersion()"
    />
  </PageWrapper>
</template>

<style module lang="scss">
.enrollments, .channels {
  gap: 0;
  margin-left: -1rem;
  margin-right: -1rem;
  width: calc(100% + 2rem);
  @media (min-width: 1024px) {
    gap: 1rem;
    margin: 0;
    margin-top: 0.5rem;
    width: 100%;
  }
}
.heading {
  margin-top: 2rem;
}
.searchAndFilter {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  @media (min-width: 768px) {
    gap: 0.5rem;
    flex-direction: row;
    justify-content: space-between;
    margin-top: -0.5rem;
  }
}
.filter, .search {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.search {
  flex: 1;
  small {
    display: none;
    @media (min-width: 768px) {
      display: block;
    }
  }
}
.filter {
  @media (min-width: 768px) {
    flex: 0 0 20rem;
  }
}
.appVersionIcon {
  margin-top: 1rem;
  color: var(--color-elements);
  cursor: pointer;
}
</style>
